/**
 * Componente raíz de la aplicación — Define el Router y proveedores globales
 * Rutas con React.lazy para reducir JS inicial (Lighthouse / /landing).
 *
 * Las rutas protegidas (roles/módulo/plan) se generan desde
 * `@/router/routeCatalog` — la ÚNICA fuente de verdad de permisos por ruta.
 * Las páginas públicas y de error se declaran aquí directamente.
 */
import {
  lazy,
  Suspense,
  type ComponentType,
  type LazyExoticComponent,
} from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "@/router/ProtectedRoute";
import HomeRedirect from "@/router/HomeRedirect";
import { APP_ROUTES } from "@/router/routeCatalog";

import MainLayout from "@/components/layouts/MainLayout";
import AuthLayout from "@/components/layouts/AuthLayout";
import SocialOnboardingLayout from "@/components/layouts/SocialOnboardingLayout";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { OperationalPermissionsGate } from "@/components/common/OperationalPermissionsGate";
import { SkeletonTiendaPublicPage, Skeleton } from "@/components/ui";
import { AuditNotificationHandler } from "@/features/auditoria/components/AuditNotificationHandler";

import { NotFoundPage, ForbiddenPage, ServerErrorPage } from "@/pages/error";

function LandingPageSkeleton() {
  return (
    <div
      className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6 space-y-6 animate-fadeIn"
      role="status"
      aria-busy="true"
    >
      <Skeleton width="w-48" height="h-10" rounded="lg" />
      <Skeleton width="w-full max-w-2xl" height="h-6" rounded="md" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} width="w-full" height="h-32" rounded="xl" />
        ))}
      </div>
    </div>
  );
}

function PublicPageSkeleton() {
  return (
    <div
      className="min-h-[50vh] p-6 flex flex-col items-center justify-center gap-4 animate-fadeIn"
      role="status"
      aria-busy="true"
    >
      <Skeleton width="w-64" height="h-8" rounded="md" />
      <Skeleton width="w-full max-w-md" height="h-4" rounded="md" />
      <Skeleton width="w-40" height="h-10" rounded="lg" />
    </div>
  );
}

const LandingPage = lazy(() => import("@/pages/landing"));
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"));
const SocialOnboardingPage = lazy(
  () => import("@/pages/auth/SocialOnboardingPage"),
);
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));
const ForgotPasswordPage = lazy(
  () => import("@/pages/auth/ForgotPasswordPage"),
);
const CajaPage = lazy(() => import("@/pages/caja/CajaPage"));
const CajaVentasPage = lazy(() => import("@/pages/caja/CajaVentasPage"));
const CajaCierresPage = lazy(() => import("@/pages/caja/CajaCierresPage"));
const CajaMovimientosPage = lazy(
  () => import("@/pages/caja/CajaMovimientosPage"),
);
const CajaCreditosPage = lazy(() => import("@/pages/caja/CajaCreditosPage"));
const FidelizacionPage = lazy(() => import("@/pages/caja/FidelizacionPage"));
const InventoryPage = lazy(() => import("@/pages/inventory/InventoryPage"));
const PersonalPage = lazy(() => import("@/pages/personal/PersonalPage"));
const PersonalTareasPage = lazy(
  () => import("@/pages/personal/PersonalTareasPage"),
);
const PersonalAsistenciaPage = lazy(
  () => import("@/pages/personal/PersonalAsistenciaPage"),
);
const NominaPage = lazy(() => import("@/pages/personal/NominaPage"));
const PersonalCalendarioPage = lazy(
  () => import("@/pages/personal/PersonalCalendarioPage"),
);
const PersonalVacationsPage = lazy(
  () => import("@/pages/personal/PersonalVacationsPage"),
);
const HigienePage = lazy(() => import("@/pages/higiene/HigienePage"));
const HigieneAreaPage = lazy(() => import("@/pages/higiene/HigieneAreaPage"));
const MisTareasPage = lazy(() => import("@/pages/personal/MisTareasPage"));
const MiAsistenciaPage = lazy(
  () => import("@/pages/personal/MiAsistenciaPage"),
);
const MyPayrollPage = lazy(() => import("@/pages/personal/MyPayrollPage"));
const MyProfilePage = lazy(() => import("@/pages/personal/MyProfilePage"));
const AuditoriaPage = lazy(() => import("@/pages/auditoria/AuditoriaPage"));
const MetricasPage = lazy(() => import("@/pages/metrics/MetricasPage"));
const MetricasSectionPage = lazy(
  () => import("@/pages/metrics/MetricasSectionPage"),
);
const MetricasAnalysisPage = lazy(
  () => import("@/pages/metrics/MetricasAnalysisPage"),
);
const IAPage = lazy(() => import("@/pages/ia/IAPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const TiendaPublicPage = lazy(() => import("@/pages/tienda/TiendaPublicPage"));
const PedidosPage = lazy(() => import("@/pages/tienda/PedidosPage"));
const TiendaConfiguracionPage = lazy(
  () => import("@/pages/tienda/TiendaConfiguracionPage"),
);
const TiendaAsistenteConfigPage = lazy(
  () => import("@/pages/tienda/TiendaAsistenteConfigPage"),
);
const PromocionesPage = lazy(() => import("@/pages/inventory/PromocionesPage"));
const DomiciliarioLiquidacionesPage = lazy(
  () => import("@/pages/tienda/DomiciliarioLiquidacionesPage"),
);
const PremiosCatalogoPage = lazy(
  () => import("@/pages/fidelizacion/PremiosCatalogoPage"),
);
const JuegosPublicoPage = lazy(
  () => import("@/pages/fidelizacion/JuegosPublicoPage"),
);
const VacantePublicPage = lazy(
  () => import("@/pages/vacantes/VacantePublicPage"),
);
const CalificacionPublicPage = lazy(
  () => import("@/pages/calificaciones/CalificacionPublicPage"),
);
const CalificacionesAdminPage = lazy(
  () => import("@/pages/calificaciones/CalificacionesAdminPage"),
);
const VacantesPage = lazy(() => import("@/pages/personal/VacantesPage"));
const BuzonEquipoPage = lazy(() => import("@/pages/personal/BuzonEquipoPage"));
const MeseroPage = lazy(() => import("@/pages/mesas/MeseroPage"));
const OrdenesMesaPage = lazy(() => import("@/pages/mesas/OrdenesMesaPage"));
const CocinaPage = lazy(() => import("@/pages/mesas/CocinaPage"));
const MesasConfigPage = lazy(() => import("@/pages/mesas/MesasConfigPage"));
const DemoEntryPage = lazy(() => import("@/pages/demo/DemoEntryPage"));

/**
 * Mapa path → página lazy para las rutas protegidas del catálogo.
 * PersonalPage se renderiza aparte porque recibe props (lockedTab, etc.).
 */
const PAGE_COMPONENTS: Record<string, LazyExoticComponent<ComponentType>> = {
  "/dashboard": DashboardPage,
  "/caja": CajaPage,
  "/caja/ventas": CajaVentasPage,
  "/caja/cierres": CajaCierresPage,
  "/caja/movimientos": CajaMovimientosPage,
  "/caja/creditos": CajaCreditosPage,
  "/caja/fidelizacion": FidelizacionPage,
  "/inventario": InventoryPage,
  "/inventario/promociones": PromocionesPage,
  "/personal": PersonalPage,
  "/personal/tareas": PersonalTareasPage,
  "/personal/asistencia": PersonalAsistenciaPage,
  "/personal/nomina": NominaPage,
  "/personal/vacantes": VacantesPage,
  "/personal/vacaciones": PersonalVacationsPage,
  "/personal/calendario": PersonalCalendarioPage,
  "/calificaciones": CalificacionesAdminPage,
  "/buzon-equipo": BuzonEquipoPage,
  "/higiene": HigienePage,
  "/higiene/:areaId": HigieneAreaPage,
  "/mis-tareas": MisTareasPage,
  "/mi-asistencia": MiAsistenciaPage,
  "/mi-nomina": MyPayrollPage,
  "/mi-perfil": MyProfilePage,
  "/auditoria": AuditoriaPage,
  "/metricas": MetricasPage,
  "/metricas/analisis/:cardId": MetricasAnalysisPage,
  "/metricas/:sectionId": MetricasSectionPage,
  "/ia": IAPage,
  "/ia/:tab": IAPage,
  "/mesas": MeseroPage,
  "/mesas/ordenes": OrdenesMesaPage,
  "/mesas/cocina": CocinaPage,
  "/mesas/configuracion": MesasConfigPage,
  "/tienda/pedidos": PedidosPage,
  "/tienda/configuracion": TiendaConfiguracionPage,
  "/tienda/configuracion/asistente": TiendaAsistenteConfigPage,
  "/tienda/liquidaciones": DomiciliarioLiquidacionesPage,
};

function ProtectedPageElement({ path }: { path: string }) {
  const Page = PAGE_COMPONENTS[path];
  // PersonalPage recibe props del layout de Personal (única página con props).
  if (path === "/personal") {
    return (
      <PersonalPage
        lockedTab="empleados"
        showTabs={false}
        headerTitle="Personal"
      />
    );
  }
  return <Page />;
}

function App() {
  return (
    <>
      <AuditNotificationHandler />
      <ErrorBoundary>
        <Routes>
          <Route
            path="/landing"
            element={
              <Suspense fallback={<LandingPageSkeleton />}>
                <LandingPage />
              </Suspense>
            }
          />

          <Route
            path="/demo"
            element={
              <Suspense fallback={<PublicPageSkeleton />}>
                <DemoEntryPage />
              </Suspense>
            }
          />

          <Route
            path="/tienda/:slug"
            element={
              <Suspense fallback={<SkeletonTiendaPublicPage />}>
                <TiendaPublicPage />
              </Suspense>
            }
          />
          <Route
            path="/tienda/:slug/producto/:productoId"
            element={
              <Suspense fallback={<SkeletonTiendaPublicPage />}>
                <TiendaPublicPage />
              </Suspense>
            }
          />
          <Route
            path="/tienda/:slug/checkout"
            element={
              <Suspense fallback={<SkeletonTiendaPublicPage />}>
                <TiendaPublicPage />
              </Suspense>
            }
          />
          <Route
            path="/tienda/:slug/pedido/:pedidoId"
            element={
              <Suspense fallback={<SkeletonTiendaPublicPage />}>
                <TiendaPublicPage />
              </Suspense>
            }
          />
          <Route
            path="/tienda/:slug/pedido/:pedidoId/recibido"
            element={
              <Suspense fallback={<SkeletonTiendaPublicPage />}>
                <TiendaPublicPage />
              </Suspense>
            }
          />

          <Route
            path="/puntos/:slug"
            element={
              <Suspense fallback={<PublicPageSkeleton />}>
                <PremiosCatalogoPage />
              </Suspense>
            }
          />

          <Route
            path="/puntos/:slug/juegos"
            element={
              <Suspense fallback={<PublicPageSkeleton />}>
                <JuegosPublicoPage />
              </Suspense>
            }
          />

          <Route
            path="/vacantes/:slug"
            element={
              <Suspense fallback={<PublicPageSkeleton />}>
                <VacantePublicPage />
              </Suspense>
            }
          />

          <Route
            path="/califica/:slug"
            element={
              <Suspense fallback={<PublicPageSkeleton />}>
                <CalificacionPublicPage />
              </Suspense>
            }
          />
          <Route
            path="/califica/:slug/:empleadoId"
            element={
              <Suspense fallback={<PublicPageSkeleton />}>
                <CalificacionPublicPage />
              </Suspense>
            }
          />

          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          <Route element={<SocialOnboardingLayout />}>
            <Route
              path="/onboarding-social"
              element={<SocialOnboardingPage />}
            />
          </Route>

          <Route
            element={
              <ProtectedRoute>
                <OperationalPermissionsGate>
                  <MainLayout />
                </OperationalPermissionsGate>
              </ProtectedRoute>
            }
          >
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomeRedirect />
                </ProtectedRoute>
              }
            />

            <Route
              path="/historial-ventas"
              element={<Navigate to="/caja/ventas" replace />}
            />

            {/* Ruta legacy previa al renombre fiado→crédito: redirige sin romper enlaces guardados. */}
            <Route
              path="/caja/fiados"
              element={<Navigate to="/caja/creditos" replace />}
            />

            {/* Rutas protegidas generadas desde routeCatalog (única fuente de permisos) */}
            {APP_ROUTES.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ProtectedRoute
                    allowedRoles={route.roles}
                    requiredModule={route.module}
                  >
                    <Suspense fallback={<PublicPageSkeleton />}>
                      <ProtectedPageElement path={route.path} />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            ))}
          </Route>

          <Route path="/forbidden" element={<ForbiddenPage />} />

          <Route
            path="/error-500"
            element={
              <ServerErrorPage errorDetail="Error de ejemplo: Cannot read properties of undefined (reading 'map')" />
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
    </>
  );
}

export default App;
